import React from "react";

import {ExternalLink} from "../../component/IconLink";
import ImgGallery from "../../component/ImgGallery";
import Link from "../../component/Link";
import Ribbon from "../../component/Ribbon";
import Video from "../../component/Video";
import Code from "../../component/Code";
import {SpecificTag} from "../../views/Tag";

export default function PageContent() {
    return (<>
        <section>
            <h2>Marketing Campaign - Round 2</h2>
            <p>
                As I had already taken up the responsibility of <SpecificTag name="marketing" showCount={true} /> and <SpecificTag name="design" showCount={true} /> for <Link to="/project/hadiko-heimfest-2022">last year's Heimfest</Link> in the <ExternalLink href="https://hadiko.de">HaDiKo student dormitory</ExternalLink>, I gladly accepted doing it again for this year's celebration.
                Having a blueprint for all required marketing materials, knowing relevant people and being aware of pitfalls from last year's experiences significantly sped up my process.
                In fact, this year's marketing campaign was even more extensive, incorporating more online-marketing and using an ambitious design language.
            </p>
            <ImgGallery images={[
                {
                    src : require("../../../media/projects/hadiko-heimfest-2023/2023-05-20-Heimfest-19-47-05-1920.jpg"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2023/2023-05-20-Heimfest-19-47-05-540.jpg"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2023/2023-05-20-Heimfest-19-47-05-32.jpg"),
                    alt : "The grounds during the day",
                    title : "The Heimfest in the first few hours."
                },
                {
                    src : require("../../../media/projects/hadiko-heimfest-2023/2023-05-20-Heimfest-20-28-46-1920.jpg"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2023/2023-05-20-Heimfest-20-28-46-540.jpg"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2023/2023-05-20-Heimfest-20-28-46-32.jpg"),
                    alt : "The stage of the Heimfest during daytime",
                    title : "Some of my friends came over to visit."
                },{
                    src : require("../../../media/projects/hadiko-heimfest-2023/2023-05-20-Heimfest-21-49-47-1920.jpg"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2023/2023-05-20-Heimfest-21-49-47-540.jpg"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2023/2023-05-20-Heimfest-21-49-47-32.jpg"),
                    alt : "The stage by night",
                    title : "The stage in the last few hours of the Heimfest."
                }
            ]} />
            <p>
                I was originally considering re-using last year's design at least partially to create a general Heimfest design language that could be used in the years to come - but instead I decided on designing an entirely new identity.
            </p>

        </section>


        <section>
            <h2>Generating the background</h2>
            <p>
                Similar to <Link to="/project/hadiko-heimfest-2022">last year's Heimfest</Link> I decided on an algorithmically generated background based on simple shapes.
                I was also planning on using the background in videos and animations for social media content.
                After some creative trial-and-error I settled on circles, that change colors based on how many circle overlaps there are.
                It's kind of hard to explain, so just take a look:
            </p>
            <Ribbon color="primary">
                <div className="center--horizontal">
                    <Video
                        src={require("../../../media/projects/hadiko-heimfest-2023/circle-shader.webm")}
                        srcFallback={require("../../../media/projects/hadiko-heimfest-2023/circle-shader-firstframe.png")}
                        maxWidth="400px"
                        scrollIn={true}
                    />
                </div>
            </Ribbon>
            <p>
                You can also think of the algorithm like counting overlapping circles <i>per pixel</i>.
                Since I was planning on creating high-resolution images for print and even videos with many frames, I quickly realized I didn't want to burden my CPU with such a tedious task.
                Also, experimenting with parameters would be no fun if I had to wait seconds to minutes every time.
            </p>
            <p>
                So what do you do if your CPU can't handle your graphical task? Just use the GPU instead!
                A <SpecificTag name="shaders" overwriteTitle="Shader" showCount={true} /> is actually the perfect fit for most &lsquo;per-pixel tasks&rsquo;, so that's what I decided on.
                Instead of creating a C++ or C# project and setting up an openGL context I fired up the <ExternalLink href="https://www.godotengine.org/">Godot game engine</ExternalLink> to get started right away.
                I setup a scene that allowed me to render a shader on any resolution and to parameterize and randomly generate the circle positions for the pattern.
                The shader itself is written in the <ExternalLink href="https://docs.godotengine.org/en/stable/tutorials/shaders/shader_reference/shading_language.html">Godot shading language</ExternalLink> which is very similar to the <ExternalLink href="https://registry.khronos.org/OpenGL/specs/es/3.0/GLSL_ES_Specification_3.00.pdf">OpenGL shading language</ExternalLink>.
                After passing the (potentially thousands) of circle positions to shader, the shader was relatively straight-forward:
            </p>
            <Code language="glsl" languageIndicator="Godot shading language" content={`shader_type canvas_item;

// constants
const int MAX_CIRCLES = 1024;
const int EMPTY = -1337; // arbitrary end signal

// circle positions and other uniforms
uniform int[MAX_CIRCLES] pos_x;
uniform int[MAX_CIRCLES] pos_y;
uniform int[MAX_CIRCLES] radii_sq;
uniform ivec2 pixel_size;
uniform int color_offset = 0;

// color palette
const int NUM_COLS = 7;
const vec4[] COLORS = {
    vec4(0.98431372642517, 0.96078431606293, 0.84313726425171, 1.0),
    vec4(0.96862745285034, 0.57254904508591, 0.32156863808632, 1.0),
    vec4(0.97647058963776, 0.5686274766922, 0.3137255012989, 1.0),
    vec4(0.84705883264542, 0.14901961386204, 0.37647059559822, 1.0),
    vec4(0.3098039329052, 0.1294117718935, 0.36862745881081, 1.0),
    vec4(0.19215686619282, 0.37254902720451, 0.58039218187332, 1.0),
    vec4(0.19215686619282, 0.73333334922791, 0.68627452850342, 1.0)
};

// fragment shader
void fragment() {
    ivec2 pos = ivec2(vec2(pixel_size) * UV);
    
    // iterate circles and count overlaps
    int count = 0;
    for (int i = 0; i < MAX_CIRCLES; i++) {
        // abort if end signal found
        if (pos_x[i] == EMPTY) {
            break;
        }
        // calc distance and check if in circle
        ivec2 circle_pos = ivec2(pos_x[i], pos_y[i]);
        int dx = pos.x - circle_pos.x;
        int dy = pos.y - circle_pos.y;
        if (dx * dx + dy * dy < radii_sq[i]) {
            count += 1;
        }
    }
    
    // set output color according to count and color palette
    int count_mod = (count + color_offset) % NUM_COLS;
    COLOR = COLORS[count_mod];
}`} />

            <p>
                With this tool in my bag, generating backgrounds according to my needs during the marketing campaign was, as I had hoped, a breeze.
                Here are a couple example patterns:
            </p>

            <ImgGallery cols={3} images={[
                {
                    src : require("../../../media/projects/hadiko-heimfest-2023/pattern_0-1920.png"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2023/pattern_0-540.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2023/pattern_0-32.png"),
                    alt : "Generated circle pattern 1",
                    title : "This one somewhat resembles the pattern used for the poster."
                },{
                    src : require("../../../media/projects/hadiko-heimfest-2023/pattern_1-1920.png"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2023/pattern_1-540.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2023/pattern_1-32.png"),
                    alt : "Generated circle pattern 2",
                    title : "The circle positions can also be on a regular grid."
                },{
                    src : require("../../../media/projects/hadiko-heimfest-2023/pattern_2-1920.png"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2023/pattern_2-540.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2023/pattern_2-32.png"),
                    alt : "Generated circle pattern 3",
                    title : "By messing with the total number of circles and their radii, the average number of circle overlaps and hence general color vibe can differ greatly."
                },{
                    src : require("../../../media/projects/hadiko-heimfest-2023/pattern_3-1920.png"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2023/pattern_3-540.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2023/pattern_3-32.png"),
                    alt : "Generated circle pattern 4",
                    title : "Fine-tuning the parameters sometimes lead to interesting results."
                },{
                    src : require("../../../media/projects/hadiko-heimfest-2023/pattern_4-1920.png"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2023/pattern_4-540.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2023/pattern_4-32.png"),
                    alt : "Generated circle pattern 5",
                    title : "Similar to the last but with greater radii."
                },{
                    src : require("../../../media/projects/hadiko-heimfest-2023/pattern_5-1920.png"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2023/pattern_5-540.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2023/pattern_5-32.png"),
                    alt : "Generated circle pattern 6",
                    title : "This one is almost on a regular grid but has slight imprecisions."
                }
            ]} />
            <p>
                By changing the circles' positions and radii each frame and updating the shader's uniforms, the tool allows for creating videos as well.
                I just had to make sure to output a video frame by frame with a constant frame rate to prevent stuttering.
                We actually used these hallucination-inducing videos for save-the-date invitations on messengers.
            </p>
            <p>
                The dormitory has a couple screens installed in entrance areas to advertise for services and upcoming events.
                Bright colorful moving circles proved quite effective on these screens and were yet another way for me to advertise.
            </p>
            <Ribbon color="primary">
                <div className="center--horizontal">
                    <Video
                        src={require("../../../media/projects/hadiko-heimfest-2023/headspace.webm")}
                        srcFallback={require("../../../media/projects/hadiko-heimfest-2023/headspace-firstframe.png")}
                        maxWidth="600px"
                        scrollIn={true}
                    />
                </div>
            </Ribbon>
        </section>

        <section>
            <h2>Creating the promotional material</h2>
            <p>
                The background pattern and its color palette have a strong character, making it recognizable.
                At the same time, the high contrast and &lsquo;noise&rsquo; make placing text or images directly on the pattern virtually impossible, making it hard to work with.
                To create a meaningful and useful design I had to find visual elements that would increase the contrast.
            </p>
            <p>
                White boxes with negative text or white borders are the obvious choice,
                but what really gave the design that special something was the purple-tinted blur:
            </p>
            <ImgGallery images={[
                {
                    src : require("../../../media/projects/hadiko-heimfest-2023/collage-material.png"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2023/collage-material-1080.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2023/collage-material-32.png"),
                    alt : "Promotional material, tickets and badges",
                    title : "Overview of promotional print material."
                }
            ]} />
            <p>
                Creating the poster took the most time, since it was a highly iterative process and I tried including the orga's ideas and feedback.
                With the poster as a base for the design, creating the rest of the material was a matter of copy-and-paste and adjust.
            </p>
            <p>
                When the ordered material finally arrived, I was quite happy with the result!
                Getting myself some helpers and gearing up with glue and brushes, we started our journey across Karlsruhe to put up:
            </p>
            <ul className="ul--text">
                <li>75 DIN A2 posters</li>
                <li>25 DIN A3 posters</li>
                <li>1 banner on the uni canteen</li>
            </ul>
            <ImgGallery images={[
                {
                    src : require("../../../media/projects/hadiko-heimfest-2023/putting-up-posters-0-1920.jpg"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2023/putting-up-posters-0-540.jpg"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2023/putting-up-posters-0-32.jpg"),
                    alt : "Posters just after they arrived",
                    title : "The long-awaited arrival of 75 A2 posters and 25 A3 posters."
                }, {
                    src : require("../../../media/projects/hadiko-heimfest-2023/putting-up-posters-1-1920.jpg"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2023/putting-up-posters-1-540.jpg"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2023/putting-up-posters-1-32.jpg"),
                    alt : "The banner just after it arrived",
                    title : "Three times one meter is larger than I thought! This banner was put up above the uni canteen."
                }, {
                    src : require("../../../media/projects/hadiko-heimfest-2023/putting-up-posters-2-1920.jpg"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2023/putting-up-posters-2-540.jpg"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2023/putting-up-posters-2-32.jpg"),
                    alt : "Transporting the posters in a wagon",
                    title : "Putting up the posters is more fun as a group, so we set out into the city with everything we needed in a wagon."
                }, {
                    src : require("../../../media/projects/hadiko-heimfest-2023/putting-up-posters-3-1920.jpg"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2023/putting-up-posters-3-540.jpg"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2023/putting-up-posters-3-32.jpg"),
                    alt : "Putting up a poster",
                    title : "Advertising on the uni campus was especially important..."
                }
            ]} />
            <p>
                We made sure to distribute posters at the multiple universities, in other student dormitories and places that students frequent.
                The trips were actually fun, especially when taking the right people along!
                Getting the permissions to put up the posters though was tedious, but worthwhile.
            </p>
            <p>
                Just a week before the Heimfest I also advertised on the university's <ExternalLink href="https://www.reddit.com/r/KaIT/">subreddit</ExternalLink>.
                Actually, it was stealth marketing. You see, we managed to get the [kœri]werk on board the Heimfest, which usually serves curry wurst in the university's canteen.
                Besides selling good curry wurst and having great marketing, they are generally very popular and have the highest meme value on the subreddit
                (in fact, as of writing this, [kœri]werk is emblazoned on the cover photo of what's supposed to be the university's subreddit).
                So in this <ExternalLink href="https://www.reddit.com/r/KaIT/comments/138na5y/wie_man_kann_das_k%C5%93riwerk_auch_f%C3%BCr_feste_mieten/">reddit post</ExternalLink> I just happened to mention that the [kœri]werk would be selling at the Heimfest ;)
            </p>
        </section>

        <section>
            <h2>Signposting the Heimfest</h2>
            <p>
                With the presets from last year and a fully developed design guide, creating the signage was routine work.
                A total of 37 different signs: Price lists, directions, band line up, rules and more - all designed, printed and laminated in one (exhausting) weekend:
            </p>
            <ImgGallery images={[
                {
                    src : require("../../../media/projects/hadiko-heimfest-2023/collage-signs.png"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2023/collage-signs-1080.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2023/collage-signs-32.png"),
                    alt : "Signage for during the Heimfest",
                    title : "Compilation of some of the signs including price lists, rules, information, directions and more."
                }
            ]} />
        </section>

        <section>
            <h2>Summary</h2>
            <p>
                Did I mention the Heimfest was a complete success?
                During the first hours, people had food, met up and talked. By the end they (and I) were mosh pitting to our headliners before the stage illuminated in (semi) professional lighting.
                The highest guest count by the security was at 1600, the cumulative count is estimated at 2000.
                All the finances are calculated to make no profit, but we made some profit anyways (which will find its way to the self-administration)!
            </p>
            <ImgGallery images={[
                {
                    src : require("../../../media/projects/hadiko-heimfest-2023/2023-05-20-Heimfest-22-30-38-1920.jpg"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2023/2023-05-20-Heimfest-22-30-38-540.jpg"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2023/2023-05-20-Heimfest-22-30-38-32.jpg"),
                    alt : "The headliner of the Heimfest 2023: The Big Bs",
                    title : "The forth act was this Heimfests headliner: The Big Bs."
                },
                {
                    src : require("../../../media/projects/hadiko-heimfest-2023/2023-05-20-Heimfest-22-46-27-1920.jpg"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2023/2023-05-20-Heimfest-22-46-27-540.jpg"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2023/2023-05-20-Heimfest-22-46-27-32.jpg"),
                    alt : "Professional lighting of the stage",
                    title : "The light technicians really pulled through and put together a professional lighting show."
                },{
                    src : require("../../../media/projects/hadiko-heimfest-2023/2023-05-20-Heimfest-22-46-53-1920.jpg"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2023/2023-05-20-Heimfest-22-46-53-540.jpg"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2023/2023-05-20-Heimfest-22-46-53-32.jpg"),
                    alt : "The atmosphere in front of the stage was electric",
                    title : "The atmosphere in front of the stage was perfect by the last act. Some people would even mosh-pit!"
                }
            ]} />
            <p>
                Looking back at my work there, I can't help but feel a little proud to have brought more than enough guests onto the grounds.
                It's incredible that we as a team put up this professional concert in our free time.
                I'm also quite happy to know a lot of people would be able to instantly recognize the design and associate it with the Heimfest!
            </p>
        </section>
    </>);
}
